import { lazy, Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Loader2 } from "lucide-react";

import "./i18n";
import { AuthProvider } from "@/contexts/AuthContext";
import { CompanyProvider } from "@/contexts/CompanyContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import { usePageTitle } from "@/hooks/usePageTitle";
import { DirtyNavProvider } from "@/contexts/DirtyNavContext";
import { GlobalErrorBoundary } from "@/components/GlobalErrorBoundary";
import ScrollToTop from "@/components/ScrollToTop";
import { DefaultLangRoot, LangQueryRedirect, LangRoot } from "@/components/LangRoutes";
import { PlanFeatureGate } from "@/components/subscription/PlanFeatureGate";
import { subscribeJournalListInvalidation } from "@/lib/journalListCache";
import { subscribeCompanyAccountsInvalidation } from "@/hooks/useCompanyAccounts";

const PageTitleUpdater = () => { usePageTitle(); return null; };

// Layouts are needed for parent routes — keep eager so children mount instantly.
import { AppLayout } from "@/components/layout/AppLayout";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import MarketingHome from "./pages/marketing/Home";

// Public auth (small + frequently first paint)
const Auth = lazy(() => import("./pages/Auth"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Unauthorized = lazy(() => import("./pages/Unauthorized"));
const InvitationAccept = lazy(() => import("./pages/InvitationAccept"));
const ShortLinkRedirect = lazy(() => import("./pages/ShortLinkRedirect"));
const ChangePassword = lazy(() => import("./pages/ChangePassword"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));

// Marketing
const MarketingPricing = lazy(() => import("./pages/marketing/Pricing"));
const MarketingFeatures = lazy(() => import("./pages/marketing/Features"));
const MarketingBlog = lazy(() => import("./pages/marketing/Blog"));
const MarketingBlogPost = lazy(() => import("./pages/marketing/BlogPost"));
const MarketingCompare = lazy(() => import("./pages/marketing/Compare"));
const MarketingContact = lazy(() => import("./pages/marketing/Contact"));
const MarketingLegal = lazy(() => import("./pages/marketing/Legal"));
const MarketingSecurity = lazy(() => import("./pages/marketing/Security"));
const HelpHome = lazy(() => import("./pages/help/HelpHome"));
const HelpCategory = lazy(() => import("./pages/help/HelpCategory"));
const HelpArticle = lazy(() => import("./pages/help/HelpArticle"));

// Core app
const CompaniesList = lazy(() => import("./pages/CompaniesList"));
const CompanyNew = lazy(() => import("./pages/CompanyNew"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Profile = lazy(() => import("./pages/Profile"));

// Setup
const ChartOfAccounts = lazy(() => import("./pages/setup/ChartOfAccounts"));
const CostCenters = lazy(() => import("./pages/setup/CostCenters"));
const AssetGroups = lazy(() => import("./pages/setup/AssetGroups"));
const CashDenominations = lazy(() => import("./pages/setup/CashDenominations"));
const FiscalPeriods = lazy(() => import("./pages/setup/FiscalPeriods"));
const FiscalPeriodForm = lazy(() => import("./pages/setup/FiscalPeriodForm"));
const FiscalPeriodView = lazy(() => import("./pages/setup/FiscalPeriodView"));

const TaxesAndZakat = lazy(() => import("./pages/setup/TaxesAndZakat"));
const DocumentNumbering = lazy(() => import("./pages/setup/DocumentNumbering"));
const ProductCategories = lazy(() => import("./pages/setup/ProductCategories"));
const CompanySettings = lazy(() => import("./pages/settings/CompanySettings"));
const DataExport = lazy(() => import("./pages/settings/DataExport"));
const IntegrationsPage = lazy(() => import("./pages/integrations/IntegrationsPage"));
const SallaIntegrationPage = lazy(() => import("./pages/integrations/SallaIntegrationPage"));
const ZidIntegrationPage = lazy(() => import("./pages/integrations/ZidIntegrationPage"));
const StoreCallbackPage = lazy(() => import("./pages/integrations/StoreCallbackPage"));
const MembersSettings = lazy(() => import("./pages/settings/MembersSettings"));
const AISettings = lazy(() => import("./pages/settings/AISettings"));
const RolesList = lazy(() => import("./pages/settings/RolesList"));
const RoleForm = lazy(() => import("./pages/settings/RoleForm"));
const AIAdvisor = lazy(() => import("./pages/AIAdvisor"));

// Reports
const ReportsIndex = lazy(() => import("./pages/reports/ReportsIndex"));
const TrialBalance = lazy(() => import("./pages/reports/TrialBalance"));
const Ledger = lazy(() => import("./pages/reports/Ledger"));
const IncomeStatement = lazy(() => import("./pages/reports/IncomeStatement"));
const BalanceSheet = lazy(() => import("./pages/reports/BalanceSheet"));
const CashFlowStatement = lazy(() => import("./pages/reports/CashFlowStatement"));
const TaxReturn = lazy(() => import("./pages/reports/TaxReturn"));
const PurchasesByItem = lazy(() => import("./pages/reports/purchases/PurchasesByItem"));
const PurchasePriceTrend = lazy(() => import("./pages/reports/purchases/PurchasePriceTrend"));
const VendorPriceComparison = lazy(() => import("./pages/reports/purchases/VendorPriceComparison"));
const VendorPerformance = lazy(() => import("./pages/reports/purchases/VendorPerformance"));
const APAging = lazy(() => import("./pages/reports/purchases/APAging"));
const SalesByItem = lazy(() => import("./pages/reports/sales/SalesByItem"));
const SalesByCustomer = lazy(() => import("./pages/reports/sales/SalesByCustomer"));
const SalesByCategory = lazy(() => import("./pages/reports/sales/SalesByCategory"));
const SalesTrend = lazy(() => import("./pages/reports/sales/SalesTrend"));
const ARAging = lazy(() => import("./pages/reports/sales/ARAging"));
const BelowCostSales = lazy(() => import("./pages/reports/sales/BelowCostSales"));
const LostCustomers = lazy(() => import("./pages/reports/sales/LostCustomers"));
const PriceHistory = lazy(() => import("./pages/reports/sales/PriceHistory"));
const ConsolidatedTrialBalance = lazy(() => import("./pages/reports/ConsolidatedTrialBalance"));
const ConsolidatedBalanceSheet = lazy(() => import("./pages/reports/ConsolidatedBalanceSheet"));
const ConsolidatedIncomeStatement = lazy(() => import("./pages/reports/ConsolidatedIncomeStatement"));
const ConsolidatedCashFlow = lazy(() => import("./pages/reports/ConsolidatedCashFlow"));
const InventoryMovementReport = lazy(() => import("./pages/reports/inventory/InventoryMovement"));
const ItemCardReport = lazy(() => import("./pages/reports/inventory/ItemCard"));
const StockValuationReport = lazy(() => import("./pages/reports/inventory/StockValuation"));
const NegativeStockReport = lazy(() => import("./pages/reports/inventory/NegativeStock"));
// CostReconciliationReport hidden from UI; file retained for future re-activation.
const InventoryAgingReport = lazy(() => import("./pages/reports/inventory/InventoryAging"));
const SlowMovingReport = lazy(() => import("./pages/reports/inventory/SlowMoving"));
// ReorderReport hidden from UI (route + lazy import removed). File retained at src/pages/reports/inventory/ReorderReport.tsx for future use once ProductForm exposes min_stock_level/reorder_point.
const PeriodSummaryReport = lazy(() => import("./pages/reports/inventory/PeriodSummary"));
const FixedAssetsRegisterReport = lazy(() => import("./pages/reports/assets/FixedAssetsRegister"));
const LeasesReport = lazy(() => import("./pages/reports/leases/LeasesReport"));
const PrepaidReport = lazy(() => import("./pages/reports/prepaid/PrepaidReport"));
const FinancialAnalytics = lazy(() => import("./pages/reports/analytics/FinancialAnalytics"));
const ClosingReview = lazy(() => import("./pages/closing-review/ClosingReview"));
const InventoryIntegrity = lazy(() => import("./pages/inventory/InventoryIntegrity"));
const WarehousesList = lazy(() => import("./pages/setup/WarehousesList"));
const WarehouseForm = lazy(() => import("./pages/setup/WarehouseForm"));
const DamageReasonsList = lazy(() => import("./pages/setup/DamageReasonsList"));
const DamageReasonForm = lazy(() => import("./pages/setup/DamageReasonForm"));

// Inventory wizard, GRN and Landed Costs pages are no longer routed (simplified flow).
const GoodsIssuesList = lazy(() => import("./pages/inventory/GoodsIssuesList"));
const GoodsIssueForm = lazy(() => import("./pages/inventory/GoodsIssueForm"));
const ServiceReceiptsList = lazy(() => import("./pages/inventory/ServiceReceiptsList"));
const ServiceReceiptForm = lazy(() => import("./pages/inventory/ServiceReceiptForm"));
const InventoryTransfersList = lazy(() => import("./pages/inventory/InventoryTransfersList"));
const InventoryTransferForm = lazy(() => import("./pages/inventory/InventoryTransferForm"));
const StockTakesList = lazy(() => import("./pages/inventory/StockTakesList"));
const StockTakeForm = lazy(() => import("./pages/inventory/StockTakeForm"));
const PeriodClosesList = lazy(() => import("./pages/inventory/PeriodClosesList"));
const PeriodCloseForm = lazy(() => import("./pages/inventory/PeriodCloseForm"));
const DamagesList = lazy(() => import("./pages/inventory/DamagesList"));
const DamageForm = lazy(() => import("./pages/inventory/DamageForm"));
const PendingRevaluationList = lazy(() => import("./pages/inventory/PendingRevaluationList"));

// Journal / vouchers
const JournalList = lazy(() => import("./pages/journal/JournalList"));
const JournalForm = lazy(() => import("./pages/journal/JournalForm"));
const JournalView = lazy(() => import("./pages/journal/JournalView"));
const PaymentVoucherList = lazy(() => import("./pages/vouchers/PaymentVoucherList"));
const PaymentVoucherForm = lazy(() => import("./pages/vouchers/PaymentVoucherForm"));
const PaymentVoucherView = lazy(() => import("./pages/vouchers/PaymentVoucherView"));
const ReceiptVoucherList = lazy(() => import("./pages/vouchers/ReceiptVoucherList"));
const ReceiptVoucherForm = lazy(() => import("./pages/vouchers/ReceiptVoucherForm"));
const ReceiptVoucherView = lazy(() => import("./pages/vouchers/ReceiptVoucherView"));

// Assets & expenses
const AssetsList = lazy(() => import("./pages/assets/AssetsList"));
const AssetForm = lazy(() => import("./pages/assets/AssetForm"));
const AssetView = lazy(() => import("./pages/assets/AssetView"));
const PrepaidList = lazy(() => import("./pages/prepaid/PrepaidList"));
const PrepaidForm = lazy(() => import("./pages/prepaid/PrepaidForm"));
const PrepaidView = lazy(() => import("./pages/prepaid/PrepaidView"));
const AccrualsList = lazy(() => import("./pages/accruals/AccrualsList"));
const AccrualForm = lazy(() => import("./pages/accruals/AccrualForm"));
const AccrualView = lazy(() => import("./pages/accruals/AccrualView"));
const CipList = lazy(() => import("./pages/cip/CipList"));
const CipForm = lazy(() => import("./pages/cip/CipForm"));
const CipView = lazy(() => import("./pages/cip/CipView"));
const CashCountList = lazy(() => import("./pages/cash-count/CashCountList"));
const CashCountForm = lazy(() => import("./pages/cash-count/CashCountForm"));
const CashCustodyList = lazy(() => import("./pages/cash-custody/CashCustodyList"));
const CashCustodyForm = lazy(() => import("./pages/cash-custody/CashCustodyForm"));
const CashCustodyView = lazy(() => import("./pages/cash-custody/CashCustodyView"));
const LeaseList = lazy(() => import("./pages/leases/LeaseList"));
const LeaseForm = lazy(() => import("./pages/leases/LeaseForm"));
const LeaseView = lazy(() => import("./pages/leases/LeaseView"));
const LeaseTerminationRedirect = lazy(() => import("./pages/leases/LeaseTerminationRedirect"));
const AssetCustodyTransfer = lazy(() => import("./pages/asset-custody/AssetCustodyTransfer"));
const OpeningBalances = lazy(() => import("./pages/opening-balances/OpeningBalances"));

// Billing
const Billing = lazy(() => import("./pages/billing/Billing"));
const Plans = lazy(() => import("./pages/billing/Plans"));
const Checkout = lazy(() => import("./pages/billing/Checkout"));
const PaymobReturn = lazy(() => import("./pages/billing/PaymobReturn"));

// Sales
const CustomersList = lazy(() => import("./pages/sales/CustomersList"));
const CustomerForm = lazy(() => import("./pages/sales/CustomerForm"));
const ProductsList = lazy(() => import("./pages/sales/ProductsList"));
const ProductForm = lazy(() => import("./pages/sales/ProductForm"));
const InvoicesList = lazy(() => import("./pages/sales/InvoicesList"));
const InvoiceForm = lazy(() => import("./pages/sales/InvoiceForm"));
const InvoiceView = lazy(() => import("./pages/sales/InvoiceView"));
const SalesOrdersList = lazy(() => import("./pages/sales/SalesOrdersList"));
const SalesOrderForm = lazy(() => import("./pages/sales/SalesOrderForm"));
const SalesOrderView = lazy(() => import("./pages/sales/SalesOrderView"));
const InvoicePrintSettings = lazy(() => import("./pages/sales/InvoicePrintSettings"));

// Purchases
const VendorsList = lazy(() => import("./pages/purchases/VendorsList"));
const VendorForm = lazy(() => import("./pages/purchases/VendorForm"));
const PurchaseInvoicesList = lazy(() => import("./pages/purchases/PurchaseInvoicesList"));
const VoucherAllocation = lazy(() => import("./pages/common/VoucherAllocation"));
const PurchaseInvoiceForm = lazy(() => import("./pages/purchases/PurchaseInvoiceForm"));
const PurchaseInvoiceView = lazy(() => import("./pages/purchases/PurchaseInvoiceView"));
const PurchaseOrdersList = lazy(() => import("./pages/purchases/PurchaseOrdersList"));
const PurchaseOrderForm = lazy(() => import("./pages/purchases/PurchaseOrderForm"));
const PurchaseOrderView = lazy(() => import("./pages/purchases/PurchaseOrderView"));

// Admin (rare, big — perfect for splitting)
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const SubscriptionsAdmin = lazy(() => import("./pages/admin/SubscriptionsAdmin"));
const PlansAdmin = lazy(() => import("./pages/admin/PlansAdmin"));
const UsersAdmin = lazy(() => import("./pages/admin/UsersAdmin"));
const CompaniesAdmin = lazy(() => import("./pages/admin/CompaniesAdmin"));
const AdminCompanyDetail = lazy(() => import("./pages/admin/AdminCompanyDetail"));
const AdminUserDetail = lazy(() => import("./pages/admin/AdminUserDetail"));
const AuditLog = lazy(() => import("./pages/admin/AuditLog"));
const DataIntegrity = lazy(() => import("./pages/admin/DataIntegrity"));
const StaffManagement = lazy(() => import("./pages/admin/StaffManagement"));
const ContentAdmin = lazy(() => import("./pages/admin/ContentAdmin"));
const HelpAdminList = lazy(() => import("./pages/admin/HelpAdminList"));
const HelpArticleEditor = lazy(() => import("./pages/admin/HelpArticleEditor"));
const HelpCategoryEditor = lazy(() => import("./pages/admin/HelpCategoryEditor"));
const BackupExport = lazy(() => import("./pages/admin/BackupExport"));
const NotificationsAdmin = lazy(() => import("./pages/admin/NotificationsAdmin"));
const FeatureFlagsAdmin = lazy(() => import("./pages/admin/FeatureFlagsAdmin"));
const PaymentsAdmin = lazy(() => import("./pages/admin/PaymentsAdmin"));
const BillingSettingsAdmin = lazy(() => import("./pages/admin/BillingSettingsAdmin"));
const SupportAdminList = lazy(() => import("./pages/admin/SupportAdminList"));
const SupportAdminView = lazy(() => import("./pages/admin/SupportAdminView"));
const SupportGuidesAdmin = lazy(() => import("./pages/admin/SupportGuidesAdmin"));
const HelpDraftsAdmin = lazy(() => import("./pages/admin/HelpDraftsAdmin"));

const SupportList = lazy(() => import("./pages/support/SupportList"));
const SupportNew = lazy(() => import("./pages/support/SupportNew"));
const SupportAssistantPage = lazy(() => import("./pages/support/SupportAssistantPage"));
const SupportView = lazy(() => import("./pages/support/SupportView"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      retry: 1,
    },
  },
});

const RouteFallback = () => (
  <div className="flex items-center justify-center min-h-[40vh]">
    <Loader2 className="h-6 w-6 animate-spin text-primary" />
  </div>
);

// The same public pages are served under three permanent language routes.
const marketingRoutes = (lang: string) => [
  <Route key={`${lang}-home`} index element={<MarketingHome />} />,
  <Route key={`${lang}-pricing`} path="pricing" element={<MarketingPricing />} />,
  <Route key={`${lang}-features`} path="features" element={<MarketingFeatures />} />,
  <Route key={`${lang}-blog`} path="blog" element={<MarketingBlog />} />,
  <Route key={`${lang}-blogpost`} path="blog/:slug" element={<MarketingBlogPost />} />,
  <Route key={`${lang}-contact`} path="contact" element={<MarketingContact />} />,
  <Route key={`${lang}-compare`} path="compare" element={<MarketingCompare />} />,
  <Route key={`${lang}-legal`} path="legal/:kind" element={<MarketingLegal />} />,
  <Route key={`${lang}-security`} path="security" element={<MarketingSecurity />} />,
  <Route key={`${lang}-help`} path="help" element={<HelpHome />} />,
  <Route key={`${lang}-helpcat`} path="help/c/:slug" element={<HelpCategory />} />,
  <Route key={`${lang}-helpart`} path="help/:slug" element={<HelpArticle />} />,
];

// Subscribing here makes every route element re-render (not remount) on
// language change, so pages using tr() update instantly with state intact.
const App = () => {
  useTranslation();
  useEffect(() => subscribeJournalListInvalidation(queryClient), []);
  useEffect(() => subscribeCompanyAccountsInvalidation(queryClient), []);
  return (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <LangQueryRedirect />
          <AuthProvider>
            <CompanyProvider>
              <DirtyNavProvider>
              <PageTitleUpdater />
              <Suspense fallback={<RouteFallback />}>
                <GlobalErrorBoundary>
                <Routes>
                  {/* Public marketing site — permanent crawlable language routes:
                      Arabic "/", English "/en", French "/fr". */}
                  <Route element={<MarketingLayout />}>
                    <Route element={<DefaultLangRoot />}>{marketingRoutes("ar")}</Route>
                    <Route path="/en" element={<LangRoot lang="en" />}>{marketingRoutes("en")}</Route>
                    <Route path="/fr" element={<LangRoot lang="fr" />}>{marketingRoutes("fr")}</Route>
                  </Route>


                  <Route path="/auth" element={<Auth />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/unauthorized" element={<Unauthorized />} />
                  <Route path="/invitations/accept" element={<InvitationAccept />} />
                  <Route path="/s/:slug" element={<ShortLinkRedirect />} />
                  <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />

                  <Route path="/companies" element={<ProtectedRoute><CompaniesList /></ProtectedRoute>} />
                  <Route path="/companies/new" element={<ProtectedRoute><CompanyNew /></ProtectedRoute>} />

                  <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/setup/accounts" element={<ChartOfAccounts />} />
                    <Route path="/setup/cost-centers" element={<CostCenters />} />
                    <Route path="/setup/cash-denominations" element={<CashDenominations />} />
                    <Route path="/setup/asset-groups" element={<AssetGroups />} />
                    <Route path="/setup/fiscal-periods" element={<FiscalPeriods />} />
                    <Route path="/setup/fiscal-periods/new" element={<FiscalPeriodForm />} />
                    <Route path="/setup/fiscal-periods/:id" element={<FiscalPeriodView />} />
                    
                    <Route path="/setup/taxes-zakat" element={<TaxesAndZakat />} />
                    <Route path="/setup/document-numbering" element={<DocumentNumbering />} />
                    <Route path="/setup/product-categories" element={<ProductCategories />} />
                    <Route path="/settings/company" element={<CompanySettings />} />
                    <Route path="/settings/members" element={<MembersSettings />} />
                    <Route path="/settings/ai" element={<AISettings />} />
                    <Route path="/settings/export" element={<DataExport />} />
                    <Route path="/settings/roles" element={<RolesList />} />
                    <Route path="/settings/roles/new" element={<RoleForm />} />
                    <Route path="/settings/roles/:id" element={<RoleForm />} />
                    <Route path="/integrations" element={<IntegrationsPage />} />
                    <Route path="/integrations/salla" element={<SallaIntegrationPage />} />
                    <Route path="/integrations/salla/callback" element={<StoreCallbackPage platform="salla" />} />
                    <Route path="/integrations/zid" element={<ZidIntegrationPage />} />
                    <Route path="/integrations/zid/callback" element={<StoreCallbackPage platform="zid" />} />
                    <Route path="/ai" element={<AIAdvisor />} />
                    <Route path="/ai/:conversationId" element={<AIAdvisor />} />
                    <Route path="/reports" element={<ReportsIndex />} />
                    <Route path="/reports/trial-balance" element={<TrialBalance />} />
                    <Route path="/reports/ledger" element={<Ledger />} />
                    <Route path="/reports/income-statement" element={<IncomeStatement />} />
                    <Route path="/reports/balance-sheet" element={<BalanceSheet />} />
                    <Route path="/reports/cash-flow" element={<CashFlowStatement />} />
                    <Route path="/reports/tax-return" element={<TaxReturn />} />
                    <Route path="/reports/purchases/by-item" element={<PurchasesByItem />} />
                    <Route path="/reports/purchases/price-trend" element={<PurchasePriceTrend />} />
                    <Route path="/reports/purchases/vendor-comparison" element={<VendorPriceComparison />} />
                    <Route path="/reports/purchases/vendor-performance" element={<VendorPerformance />} />
                    <Route path="/reports/purchases/ap-aging" element={<APAging />} />
                    <Route path="/reports/analytics" element={<PlanFeatureGate feature="reports.analytics"><FinancialAnalytics /></PlanFeatureGate>} />
                    <Route path="/closing-review" element={<PlanFeatureGate feature="finance.closing_review"><ClosingReview /></PlanFeatureGate>} />
                    <Route path="/reports/sales/by-item" element={<SalesByItem />} />
                    <Route path="/reports/sales/by-customer" element={<SalesByCustomer />} />
                    <Route path="/reports/sales/by-category" element={<SalesByCategory />} />
                    <Route path="/reports/sales/trend" element={<SalesTrend />} />
                   <Route path="/reports/sales/ar-aging" element={<ARAging />} />
                   <Route path="/reports/sales/below-cost" element={<BelowCostSales />} />
                   <Route path="/reports/sales/lost-customers" element={<LostCustomers />} />
                   <Route path="/reports/sales/price-history" element={<PriceHistory />} />
                   <Route path="/reports/consolidated/trial-balance" element={<PlanFeatureGate feature="reports.consolidated.trial_balance"><ConsolidatedTrialBalance /></PlanFeatureGate>} />
                   <Route path="/reports/consolidated/balance-sheet" element={<PlanFeatureGate feature="reports.consolidated.balance_sheet"><ConsolidatedBalanceSheet /></PlanFeatureGate>} />
                  <Route path="/reports/consolidated/income-statement" element={<PlanFeatureGate feature="reports.consolidated.income_statement"><ConsolidatedIncomeStatement /></PlanFeatureGate>} />
                  <Route path="/reports/consolidated/cash-flow" element={<PlanFeatureGate feature="reports.consolidated.cash_flow"><ConsolidatedCashFlow /></PlanFeatureGate>} />
                   <Route path="/reports/inventory/movement" element={<InventoryMovementReport />} />
                   <Route path="/reports/inventory/item-card" element={<ItemCardReport />} />
                   <Route path="/reports/inventory/valuation" element={<StockValuationReport />} />
                   <Route path="/reports/inventory/negative-stock" element={<NegativeStockReport />} />
                   {/* /reports/inventory/cost-reconciliation route removed — see comment near lazy imports */}
                   <Route path="/reports/inventory/aging" element={<InventoryAgingReport />} />
                   <Route path="/reports/inventory/slow-moving" element={<SlowMovingReport />} />
                   {/* /reports/inventory/reorder route removed — see comment near lazy imports */}
                   <Route path="/reports/inventory/period-summary" element={<PeriodSummaryReport />} />
                   <Route path="/reports/assets/register" element={<FixedAssetsRegisterReport />} />
                   <Route path="/reports/leases" element={<LeasesReport />} />
                   <Route path="/reports/prepaid" element={<PrepaidReport />} />
                   <Route path="/inventory/integrity" element={<InventoryIntegrity />} />
                   <Route path="/setup/warehouses" element={<WarehousesList />} />
                   <Route path="/setup/warehouses/new" element={<WarehouseForm />} />
                   <Route path="/setup/warehouses/:id" element={<WarehouseForm />} />
                  <Route path="/setup/warehouses/:id/edit" element={<WarehouseForm />} />
                   <Route path="/setup/damage-reasons" element={<DamageReasonsList />} />
                   <Route path="/setup/damage-reasons/new" element={<DamageReasonForm />} />
                   <Route path="/setup/damage-reasons/:id" element={<DamageReasonForm />} />
                  <Route path="/setup/damage-reasons/:id/edit" element={<DamageReasonForm />} />

                   {/* Inventory wizard + GRN routes removed (simplified flow: invoice = receipt). */}
                   <Route path="/inventory/issues" element={<GoodsIssuesList />} />
                   <Route path="/inventory/issues/new" element={<GoodsIssueForm />} />
                    <Route path="/inventory/issues/:id" element={<GoodsIssueForm />} />
                   <Route path="/inventory/srn" element={<ServiceReceiptsList />} />
                   <Route path="/inventory/srn/new" element={<ServiceReceiptForm />} />
                    <Route path="/inventory/srn/:id" element={<ServiceReceiptForm />} />
                  <Route path="/inventory/transfers" element={<InventoryTransfersList />} />
                  <Route path="/inventory/transfers/new" element={<InventoryTransferForm />} />
                  <Route path="/inventory/transfers/:id" element={<InventoryTransferForm />} />
                  <Route path="/inventory/damages" element={<DamagesList />} />
                  <Route path="/inventory/damages/new" element={<DamageForm />} />
                  <Route path="/inventory/damages/:id" element={<DamageForm />} />
                   <Route path="/inventory/stock-take" element={<StockTakesList />} />
                   <Route path="/inventory/stock-take/new" element={<StockTakeForm />} />
                   <Route path="/inventory/stock-take/:id" element={<StockTakeForm />} />
                   {/* Landed costs routes removed (simplified inventory). */}
                   <Route path="/inventory/period-close" element={<PeriodClosesList />} />
                   <Route path="/inventory/period-close/new" element={<PeriodCloseForm />} />
                   <Route path="/inventory/pending-revaluation" element={<PendingRevaluationList />} />
                    <Route path="/journal" element={<JournalList />} />
                    <Route path="/opening-balances" element={<OpeningBalances />} />
                    <Route path="/journal/new" element={<JournalForm />} />
                    <Route path="/journal/:id" element={<JournalView />} />
                    <Route path="/journal/:id/edit" element={<JournalForm />} />
                    <Route path="/vouchers/payment" element={<PaymentVoucherList />} />
                    <Route path="/vouchers/payment/new" element={<PaymentVoucherForm />} />
                    <Route path="/vouchers/payment/:id" element={<PaymentVoucherView />} />
                    <Route path="/vouchers/payment/:id/edit" element={<PaymentVoucherForm />} />
                    <Route path="/vouchers/receipt" element={<ReceiptVoucherList />} />
                    <Route path="/vouchers/receipt/new" element={<ReceiptVoucherForm />} />
                    <Route path="/vouchers/receipt/:id" element={<ReceiptVoucherView />} />
                    <Route path="/vouchers/receipt/:id/edit" element={<ReceiptVoucherForm />} />
                    <Route path="/assets" element={<AssetsList />} />
                    <Route path="/assets/new" element={<AssetForm />} />
                    <Route path="/assets/:id" element={<AssetView />} />
                    <Route path="/assets/:id/edit" element={<AssetForm />} />
                    <Route path="/assets/line/:lineId/edit" element={<AssetForm />} />
                    <Route path="/prepaid" element={<PrepaidList />} />
                    <Route path="/prepaid/new" element={<PrepaidForm />} />
                    <Route path="/prepaid/:id" element={<PrepaidView />} />
                    <Route path="/prepaid/:id/edit" element={<PrepaidForm />} />
                    <Route path="/cip" element={<CipList />} />
                    <Route path="/cip/new" element={<CipForm />} />
                    <Route path="/cip/:id" element={<CipView />} />
                    <Route path="/cip/:id/edit" element={<CipForm />} />
                    <Route path="/accruals" element={<AccrualsList />} />
                    <Route path="/cash-count" element={<CashCountList />} />
                    <Route path="/cash-count/new" element={<CashCountForm />} />
                    <Route path="/cash-count/:id" element={<CashCountForm />} />
                    <Route path="/cash-custody" element={<CashCustodyList />} />
                    <Route path="/cash-custody/new" element={<CashCustodyForm />} />
                    <Route path="/cash-custody/:id" element={<CashCustodyView />} />
                    <Route path="/asset-custody-transfer" element={<AssetCustodyTransfer />} />
                    <Route path="/leases" element={<LeaseList />} />
                    <Route path="/leases/new" element={<LeaseForm />} />
                    <Route path="/leases/:id/edit" element={<LeaseForm />} />
                    <Route path="/leases/termination/:id" element={<LeaseTerminationRedirect />} />
                    <Route path="/leases/:id" element={<LeaseView />} />
                    <Route path="/accruals/new" element={<AccrualForm />} />
                    <Route path="/accruals/:id" element={<AccrualView />} />
                    <Route path="/accruals/:id/edit" element={<AccrualForm />} />
                    <Route path="/sales/customers" element={<CustomersList />} />
                    <Route path="/sales/customers/new" element={<CustomerForm />} />
                    <Route path="/sales/customers/:id" element={<CustomerForm />} />
                    <Route path="/sales/customers/:id/edit" element={<CustomerForm />} />
                    <Route path="/sales/products" element={<ProductsList />} />
                    <Route path="/sales/products/new" element={<ProductForm />} />
                    <Route path="/sales/products/:id" element={<ProductForm />} />
                    <Route path="/sales/products/:id/edit" element={<ProductForm />} />
                    <Route path="/sales/invoices" element={<InvoicesList />} />
                    <Route path="/sales/invoices/new" element={<InvoiceForm />} />
                    <Route path="/sales/invoices/:id" element={<InvoiceView />} />
                    <Route path="/sales/invoices/:id/edit" element={<InvoiceForm />} />
                    <Route path="/sales/orders" element={<SalesOrdersList />} />
                    <Route path="/sales/orders/new" element={<SalesOrderForm />} />
                    <Route path="/sales/orders/:id" element={<SalesOrderView />} />
                    <Route path="/sales/orders/:id/edit" element={<SalesOrderForm />} />
                    <Route path="/sales/print-settings" element={<InvoicePrintSettings />} />
                    <Route path="/purchases/vendors" element={<VendorsList />} />
                    <Route path="/purchases/vendors/new" element={<VendorForm />} />
                    <Route path="/purchases/vendors/:id" element={<VendorForm />} />
                    <Route path="/purchases/vendors/:id/edit" element={<VendorForm />} />
                    <Route path="/purchases/orders" element={<PurchaseOrdersList />} />
                    <Route path="/purchases/orders/new" element={<PurchaseOrderForm />} />
                    <Route path="/purchases/orders/:id" element={<PurchaseOrderView />} />
                    <Route path="/purchases/orders/:id/edit" element={<PurchaseOrderForm />} />
                    <Route path="/purchases/invoices" element={<PurchaseInvoicesList />} />
                    <Route path="/purchases/invoices/new" element={<PurchaseInvoiceForm />} />
                    <Route path="/purchases/invoices/:id" element={<PurchaseInvoiceView />} />
                    <Route path="/purchases/invoices/:id/edit" element={<PurchaseInvoiceForm />} />
                    <Route path="/purchases/allocate-payments" element={<VoucherAllocation side="ap" />} />
                    <Route path="/sales/allocate-receipts" element={<VoucherAllocation side="ar" />} />
                    <Route path="/billing" element={<Billing />} />
                    <Route path="/billing/plans" element={<Plans />} />
                    <Route path="/billing/checkout" element={<Checkout />} />
                    <Route path="/billing/paymob-return" element={<PaymobReturn />} />
                    <Route path="/support/assistant" element={<SupportAssistantPage />} />
                    <Route path="/support/assistant/:threadId" element={<SupportAssistantPage />} />
                    <Route path="/support" element={<SupportList />}>
                      <Route path="new" element={<SupportNew />} />
                      <Route path=":id" element={<SupportView />} />
                    </Route>
                  </Route>

                  {/* Internal admin portal — separate layout, role-gated, hidden from subscribers */}
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="subscriptions" element={<SubscriptionsAdmin />} />
                    <Route path="companies" element={<CompaniesAdmin />} />
                    <Route path="companies/:id" element={<AdminCompanyDetail />} />
                    <Route path="users" element={<UsersAdmin />} />
                    <Route path="users/:id" element={<AdminUserDetail />} />
                    <Route path="plans" element={<PlansAdmin />} />
                    <Route path="content" element={<ContentAdmin />} />
                    <Route path="help" element={<HelpAdminList />} />
                    <Route path="help/articles/:id" element={<HelpArticleEditor />} />
                    <Route path="help/categories/:id" element={<HelpCategoryEditor />} />
                    <Route path="audit" element={<AuditLog />} />
                    <Route path="staff" element={<StaffManagement />} />
                    <Route path="data-integrity" element={<DataIntegrity />} />
                    <Route path="backup" element={<BackupExport />} />
                    <Route path="notifications" element={<NotificationsAdmin />} />
                    <Route path="features" element={<FeatureFlagsAdmin />} />
                    <Route path="payments" element={<PaymentsAdmin />} />
                    <Route path="billing-settings" element={<BillingSettingsAdmin />} />
                    <Route path="support" element={<SupportAdminList />}>
                      <Route path=":id" element={<SupportAdminView />} />
                    </Route>
                    <Route path="support-guides" element={<SupportGuidesAdmin />} />
                    <Route path="help-drafts" element={<HelpDraftsAdmin />} />

                  </Route>

                  <Route path="*" element={<NotFound />} />
                </Routes>
                </GlobalErrorBoundary>
              </Suspense>
              <CookieConsentBanner />
              </DirtyNavProvider>
            </CompanyProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
  );
};

export default App;
